import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Tile from "../Tile";
import "./exchange-modal.css";

/**
 * ExchangeModal — lets a player select rack tiles to exchange with the bag.
 *
 * @param {boolean}    show        - Controls modal visibility.
 * @param {RackTile[]} tiles       - The player's current rack (from game.rack).
 * @param {number}     boardScale  - Board scale factor; used to size tiles consistently.
 * @param {number}     bagCount    - Number of tiles left in the bag; caps max selection.
 * @param {function}   onExchange  - Called with List<Tile> when Exchange is confirmed.
 * @param {function}   onCancel    - Called when Cancel or Modal dismiss occurs.
 */
const ExchangeModal = ({ show, tiles, boardScale, bagCount, onExchange, onCancel }) => {
    const [selectedTiles, setSelectedTiles] = useState([]);

    // Reset selection each time the modal is opened
    useEffect(() => {
        if (show) setSelectedTiles([]);
    }, [show]);

    const toggleTile = (rackTile) => {
        if (selectedTiles.includes(rackTile)) {
            // Deselect — always allowed
            setSelectedTiles(prev => prev.filter(t => t !== rackTile));
        } else if (selectedTiles.length < bagCount) {
            // Select — only if under the bag-count cap
            setSelectedTiles(prev => [...prev, rackTile]);
        }
    };

    // Match the scale formula used by the Rack component
    const scale = boardScale * 16 / 7 * 0.75;

    return (
        <Modal show={show} onHide={onCancel} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Exchange Tiles</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-center text-muted mb-3">
                    Click tiles to select them for exchange.
                    {bagCount > 0
                        ? ` You can select up to ${bagCount}.`
                        : " The bag is empty."}
                </p>
                <div className="exchange-rack" style={{ height: scale * 1.8 }}>
                    {(tiles || []).map((rackTile, i) => {
                        const selected = selectedTiles.includes(rackTile);
                        const atCap = !selected && selectedTiles.length >= bagCount;
                        return (
                            <div
                                key={`xtile-${i}`}
                                className={`exchange-tile-wrapper${atCap ? " at-cap" : ""}`}
                                style={{ width: scale, position: "relative" }}
                                onClick={atCap ? undefined : () => toggleTile(rackTile)}
                            >
                                {/* Raise selected tiles by half the tile height (scale * 0.5) */}
                                <Tile
                                    letter={rackTile.tile.letter}
                                    points={rackTile.tile.value}
                                    scale={scale}
                                    bottom={selected ? scale * 0.7 : scale * 0.2}
                                    position={0}
                                />
                            </div>
                        );
                    })}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                <Button
                    variant="primary"
                    disabled={selectedTiles.length === 0 || bagCount === 0}
                    onClick={() => onExchange(selectedTiles.map(t => t.tile))}
                >
                    Exchange{selectedTiles.length > 0 ? ` (${selectedTiles.length})` : ""}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExchangeModal;
